import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import dayjs from 'dayjs';

import { BreadcrumbHeader, DetailPanel, StatusBadge, EmptyState } from '../../components/shared';
import { vendorShipments, partReplenishmentOrders, warehouses } from '../../services/dataClient';

// ─── Location type badge ───────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const LOC_TYPE_STYLE = {
  FSL: { bg: '#E0F2F1', color: '#00695C' },
  Warehouse: { bg: '#ECEFF1', color: '#455A64' },
  'On-Site': { bg: '#FFF3E0', color: '#E65100' }
};

function LocationTypeBadge({ type }) {
  if (!type) return null;
  const style = LOC_TYPE_STYLE[type] ?? { bg: 'var(--grey-100)', color: 'var(--grey-600)' };
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex',
        alignItems: 'center',
        px: '7px',
        py: '2px',
        borderRadius: '4px',
        fontSize: '0.7rem',
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
        whiteSpace: 'nowrap'
      }}>
      
      {type}
    </Box>);

}

// ─── Status progression ───────────────────────────────────────────────────────
const STATUS_ORDER = ['Active', 'Pending', 'In Transit', 'Delivered'];

function getNextStatuses(current) {
  const idx = STATUS_ORDER.indexOf(current);
  return STATUS_ORDER.slice(idx + 1);
}

// ─── Timeline builder ─────────────────────────────────────────────────────────
function buildTimeline(shipment, extraEvents) {
  const base = dayjs(shipment.createdDate ?? dayjs().format('YYYY-MM-DD'));
  const events = [
  { label: 'Shipment Created', date: base.format('MMM D, YYYY') }];

  const status = shipment.status;
  if (status === 'Pending' || status === 'In Transit' || status === 'Delivered') {
    events.push({ label: 'Pending — Awaiting Pickup', date: base.add(1, 'day').format('MMM D, YYYY') });
  }
  if (status === 'In Transit' || status === 'Delivered') {
    events.push({ label: 'In Transit', date: base.add(2, 'day').format('MMM D, YYYY') });
  }
  if (status === 'Delivered') {
    events.push({ label: 'Delivered', date: dayjs(shipment.estimatedDeliveryDate).format('MMM D, YYYY') });
  }

  // Append any locally added events (from status updates)
  return [...events, ...extraEvents];
}

// ─── Stepper ─────────────────────────────────────────────────────────────────
function StatusStepper({ currentStatus }) {
  const currentIdx = STATUS_ORDER.indexOf(currentStatus);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', mt: 1 }}>
      {STATUS_ORDER.map((step, i) => {
        const done = i <= currentIdx;
        const current = i === currentIdx;
        return (
          <Box key={step} sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px'
              }}>
              
              <Box
                sx={{ width: 28, height: 28, borderRadius: '50%',
                  backgroundColor: done ? '#009688' : 'var(--grey-200)',
                  border: `2px solid ${done ? '#009688' : 'var(--grey-300)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                
                {done && <CheckCircleIcon sx={{ fontSize: 16, color: '#fff' }} />}
              </Box>
              <Typography
                sx={{ fontSize: '0.6875rem',
                  fontWeight: current ? 700 : 400,
                  color: done ? '#009688' : 'var(--grey-400)',
                  whiteSpace: 'nowrap'
                }}>
                
                {step}
              </Typography>
            </Box>
            {i < STATUS_ORDER.length - 1 &&
            <Box sx={{ width: 32, height: '2px', backgroundColor: i < currentIdx ? '#009688' : 'var(--grey-200)', mx: '4px', mb: '18px' }} />
            }
          </Box>);

      })}
    </Box>);

}

// ─── Linked Replenishment Order card ────────────────────────────────────────────
function LinkedPartReplenishmentCard({ replenishmentOrder }) {
  const navigate = useNavigate();
  return (
    <ContentCard
      sx={{ p: 3
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
        Linked Replenishment Order
      </Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />

      {replenishmentOrder ?
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
        { label: 'Order #', value: replenishmentOrder.orderNumber },
        { label: 'Supplier', value: replenishmentOrder.vendorName },
        { label: 'Part #', value: replenishmentOrder.partNumber },
        { label: 'Status', value: <StatusBadge status={replenishmentOrder.status} /> }]
        .map(({ label, value }) =>
        <Box key={label} sx={{ display: 'flex', gap: 2 }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', minWidth: 80, flexShrink: 0 }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>
                {value}
              </Typography>
            </Box>
        )}
          <Typography
          onClick={() => navigate(`/orders/part-replenishment/${replenishmentOrder.id}`)}
          sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', mt: 1,
            display: 'inline-flex', alignItems: 'center', gap: '4px',
            '&:hover': { textDecoration: 'underline' }
          }}>
          
            <ArrowForwardIcon sx={{ fontSize: 14 }} /> View Order
          </Typography>
        </Box> :

      <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>
          No linked replenishment order.
        </Typography>
      }
    </ContentCard>);

}

// ─── Activity Timeline ────────────────────────────────────────────────────────
function ActivityTimeline({ events }) {
  return (
    <ContentCard
      sx={{ p: 3
      }}>
      
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
        Activity Timeline
      </Typography>
      <Divider sx={{ borderColor: 'var(--grey-200)', mb: 3 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {events.map((ev, i) => {
          const isLast = i === events.length - 1;
          return (
            <Box key={`${ev.label}-${i}`} sx={{ display: 'flex', gap: '14px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 }}>
                <Box
                  sx={{ width: 12, height: 12, borderRadius: '50%', mt: '3px', flexShrink: 0,
                    backgroundColor: '#009688',
                    border: '2px solid #009688'
                  }} />
                
                {!isLast &&
                <Box sx={{ width: '2px', flex: 1, minHeight: 28, backgroundColor: 'var(--grey-200)', mt: '2px' }} />
                }
              </Box>
              <Box sx={{ pb: isLast ? 0 : 3 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                  {ev.label}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
                  {ev.date}
                </Typography>
              </Box>
            </Box>);

        })}
      </Box>
    </ContentCard>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ReplenishmentShipmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [localStatus, setLocalStatus] = useState(null);
  const [extraEvents, setExtraEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNext, setSelectedNext] = useState('');
  const [showBanner, setShowBanner] = useState(false);

  const shipment = vendorShipments.find((s) => s.id === id);

  if (!shipment) {
    return (
      <Box sx={{ p: 3 }}>
        <EmptyState
          title="Shipment not found"
          description={`No replenishment with ID "${id}" exists in the system.`} />
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography
            onClick={() => navigate('/shipments/replenishment-shipments')}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
            
            ← Back to Replenishment
          </Typography>
        </Box>
      </Box>);

  }

  const effectiveStatus = localStatus ?? shipment.status;
  const nextStatuses = getNextStatuses(effectiveStatus);
  const isDelivered = effectiveStatus === 'Delivered';

  const linkedOrder = shipment.partReplenishmentOrderId ?
  partReplenishmentOrders.find((o) => o.id === shipment.partReplenishmentOrderId) :
  null;

  const timelineEvents = buildTimeline(
    { ...shipment, status: effectiveStatus },
    extraEvents
  );

  const handleOpenModal = () => {
    setSelectedNext(nextStatuses[0] ?? '');
    setModalOpen(true);
  };

  const handleConfirm = () => {
    setLocalStatus(selectedNext);
    setExtraEvents((prev) => [
    ...prev,
    { label: `Status updated to ${selectedNext}`, date: dayjs().format('MMM D, YYYY') }]
    );
    setModalOpen(false);
    setShowBanner(true);
    setTimeout(() => setShowBanner(false), 4000);
  };

  // Resolve stock location — from shipment directly, or fall back to linked order
  const stockLocationName = shipment.stockLocation ?? linkedOrder?.stockLocation ?? '—';
  const warehouseRec = warehouses.find((w) => w.name === stockLocationName);
  const locationAddress = warehouseRec ?
  `${warehouseRec.address}, ${warehouseRec.city}, ${warehouseRec.state}` :
  null;

  const shipmentInfoFields = [
  { label: 'Shipment #', value: shipment.shipmentNumber },
  { label: 'Supplier', value: shipment.vendorName ?? '—' },
  { label: 'Carrier', value: shipment.carrier ?? '—' },
  { label: 'Tracking #', value: shipment.trackingNumber ?? '—' },
  ...(shipment.estimatedDeliveryDate ? [{ label: 'Est. Delivery', value: shipment.estimatedDeliveryDate }] : []),
  { label: 'Created Date', value: shipment.createdDate ?? '—' }];

  // Build parts list — prefer linked order's lineItems, fall back to single partNumber
  const partsRows = linkedOrder?.lineItems?.length ?
  linkedOrder.lineItems :
  [{ partNumber: shipment.partNumber ?? linkedOrder?.partNumber, partDescription: linkedOrder?.partDescription, quantity: null, unitCost: null }];

  const orderNotes = linkedOrder?.notes ?? shipment.notes ?? null;

  const stockLocationFields = [
  {
    label: 'Location',
    value:
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>{stockLocationName}</Typography>
          {warehouseRec && <LocationTypeBadge type={warehouseRec.type} />}
        </Box>

  },
  ...(locationAddress ? [{ label: 'Address', value: locationAddress }] : []),
  ...(warehouseRec?.contactName ? [{ label: 'Contact', value: warehouseRec.contactName }] : []),
  ...(warehouseRec?.contactEmail ? [{ label: 'Contact Email', value: warehouseRec.contactEmail }] : [])];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Success banner */}
      {showBanner &&
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: '10px',
          backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7',
          borderRadius: '8px', px: 2, py: '12px'
        }}>
        
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
            Status updated to {effectiveStatus}.
          </Typography>
        </Box>
      }

      {/* Header */}
      <BreadcrumbHeader
        title={shipment.shipmentNumber}
        subtitle={shipment.vendorName}
        crumbs={[
        { label: 'Shipments' },
        { label: 'Replenishment Shipments', path: '/shipments/replenishment' },
        { label: shipment.shipmentNumber }]
        } />

      {/* Two-column layout */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
          <DetailPanel title="Shipment Info" fields={shipmentInfoFields} />

          {/* Parts & Notes card */}
          <ContentCard sx={{ overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)' }}>
                Parts & Notes
              </Typography>
            </Box>
            <Divider sx={{ borderColor: 'var(--grey-200)' }} />

            {/* Parts table header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 56px 80px', px: 3, py: '8px', backgroundColor: '#eef2f6', borderBottom: '1px solid var(--grey-200)' }}>
              {['Part #', 'Description', 'Qty', 'Unit Cost'].map((h) =>
              <Typography key={h} sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-600)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {h}
                </Typography>
              )}
            </Box>

            {/* Parts rows */}
            {partsRows.map((item, i) =>
            <Box
              key={i}
              sx={{ display: 'grid',
                gridTemplateColumns: '1fr 1.6fr 56px 80px',
                px: 3,
                py: '10px',
                borderBottom: i < partsRows.length - 1 ? '1px solid var(--grey-200)' : 'none',
                alignItems: 'center'
              }}>
              
                <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)', fontWeight: 500 }}>
                  {item.partNumber ?? '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
                  {item.partDescription ?? '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)' }}>
                  {item.quantity != null ? item.quantity : '—'}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
                  {item.unitCost != null ? `$${Number(item.unitCost).toFixed(2)}` : '—'}
                </Typography>
              </Box>
            )}

            {/* Notes */}
            {orderNotes &&
            <>
                <Divider sx={{ borderColor: 'var(--grey-200)' }} />
                <Box sx={{ px: 3, py: 2 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-500)', textTransform: 'uppercase', letterSpacing: '0.04em', mb: '6px' }}>
                    Notes
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', lineHeight: 1.6 }}>
                    {orderNotes}
                  </Typography>
                </Box>
              </>
            }
          </ContentCard>

          <DetailPanel title="Stock Location" fields={stockLocationFields} />
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
          {/* Linked Replenishment Order */}
          <LinkedPartReplenishmentCard replenishmentOrder={linkedOrder} />

          {/* Shipment Status card */}
          <ContentCard
            sx={{ p: 3
            }}>
            
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--grey-900)', mb: 2 }}>
              Shipment Status
            </Typography>
            <Divider sx={{ borderColor: 'var(--grey-200)', mb: 2 }} />

            <StatusStepper currentStatus={effectiveStatus} />

            <Box sx={{ mt: 3 }}>
              {isDelivered ?
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
                  <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>
                    Shipment complete
                  </Typography>
                </Box> :

              <Button
                variant="contained"
                size="small"
                onClick={handleOpenModal}
                sx={{ backgroundColor: '#009688',
                  '&:hover': { backgroundColor: '#00796B' },
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none'
                }}>
                
                  Update Status
                </Button>
              }
            </Box>
          </ContentCard>

          {/* Activity Timeline */}
          <ActivityTimeline events={timelineEvents} />
        </Box>
      </Box>

      {/* Update Status modal */}
      <Dialog open={modalOpen} onClose={(e, reason) => {if (reason !== 'backdropClick') setModalOpen(false);}} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Update Status
          <IconButton size="small" onClick={() => setModalOpen(false)} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
            <HighlightOffIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <FormControl size="small" fullWidth>
            <InputLabel>New Status</InputLabel>
            <Select
              label="New Status"
              value={selectedNext}
              onChange={(e) => setSelectedNext(e.target.value)}>
              
              {nextStatuses.map((s) =>
              <MenuItem key={s} value={s}>{s}</MenuItem>
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography
            onClick={() => setModalOpen(false)}
            sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
            
            Cancel
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={!selectedNext}
            onClick={handleConfirm}
            sx={{ backgroundColor: '#009688',
              '&:hover': { backgroundColor: '#00796B' },
              '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              boxShadow: 'none',
              px: 2
            }}>
            
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>);

}